import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Switch from 'react-switch';

import {
    ArrayHelpers,
    FloatingMenu,
    Forms,
    Loading,
    Modal,
    SaveButton,
    StringHelpers,
    SystemText,
    Table,
    TitleBar,
} from 'e4shared';
import PageHeader from '../general/pageHeader';
import SortFilter from '../general/sortFilter';

import { clearError } from 'actions/admin';
import { addImage, setPrismMenu, setShowSuccess, toggleFormHelp } from 'actions/ui';

import config from '../../config';

import './simpleAdmin.css';

export const DefaultOptions = () => ({
    sortBy: 'name',
    ascending: true,
    showActive: false,
    showDelete: true,
    allowSetCurrent: true,
    page: 1,
    pageSize: 10,
    recordCount: 0,
    cannotDeleteMessageDefault: SystemText('Admin/General/InUse'),
    extraOptions: [],
});

export const SimpleAdmin = ({
    showModal,
    setShowModal,
    options,
    subAdd,
    defaultValues,
    fields,
    columns,
    extraButtons,
    extraAddButtons,
    blockAddNew,
    data,
    filterHelp,
    filterTextOn,
    filterColumn,
    load,
    add,
    update,
    remove,
    onUpdateLoad,
    onCancel,
    formPrefix,
    loading,
    onEdit,
    bannerMessages,
}) => {
    const dispatch = useDispatch();

    const api = {
        add: add,
        update: update,
    };

    const [entity, setEntity] = React.useState(null);
    const [show, setShow] = React.useState(false);
    const [values, setValues] = React.useState({});
    const [controlOptions, setControlOptions] = React.useState(options);
    const [errors, setErrors] = React.useState({});
    const [search, setSearch] = React.useState('');
    const [saving, setSaving] = React.useState(false);
    const [filterToggle, setFilterToggle] = React.useState(true);

    const [, updateState] = React.useState();
    const forceUpdate = React.useCallback(() => updateState({}), []);

    const user = useSelector(state => state.user.user);
    const { images, tags, handles } = useSelector(state => state.ui);
    const { errorType } = useSelector(state => state.admin);
    const adminLoading = useSelector(state => state.admin.loading);

    React.useEffect(() => {
        dispatch(setPrismMenu('Admin'));
    }, []);

    useEffect(() => {
        !controlOptions && setControlOptions(options);
    }, [options]);

    useEffect(() => {
        setSaving(false);
    }, [errorType]);

    const setCurrent = entity => {
        const values = { id: entity.id };
        fields.forEach(field => {
            values[field.dataProperty] = entity[field.dataProperty];
        });

        setEntity(entity);
        setValues(values);
        onEdit && onEdit(values);
        setErrors({});
        setShow(true);
    };

    const updateProperty = (value, dataProperty, data) => {
        const newValues = values;
        if (data) {
            let temp = newValues[dataProperty] || [];
            temp = temp.filter(t => !data.some(d => d.id === t));
            newValues[dataProperty] = temp.concat(value);
        } else {
            newValues[dataProperty] = value;
        }
        if (value instanceof File) {
            newValues[dataProperty].companyId = user.company_Id;
        }

        setValues(newValues);
        forceUpdate();
    };

    const cancel = () => {
        setValues({});
        setErrors({});
        dispatch(clearError());

        if (onCancel) {
            onCancel();
        }

        setShow(false);
        subAdd && closeModal();
    };

    const openAddModal = override => {
        const defaults = override || { ...defaultValues };
        setEntity(defaults);
        setValues(defaults);
        setShow(true);
        setErrors({});
        dispatch(clearError());
    };

    const closeModal = () => {
        setEntity(null);
        setShow(false);
        setErrors({});
        setShowModal?.(false);
        dispatch(clearError());
    };

    const validateEntity = entity => {
        let result = true;
        const errors = {};

        const checkRegEx = (value, regEx, perLine) => {
            if (perLine) {
                const lines = (value || '').split(/\r?\n/);
                return lines.every(l => !l || StringHelpers.matchRegEx(regEx, l));
            } else {
                return StringHelpers.matchRegEx(regEx, value);
            }
        };

        fields.forEach(field => {
            const value = entity[field.dataProperty];

            const validValue =
                typeof field.data === 'function' ? (field.data(values) || []).some(x => x.id === value) : value;

            const shown = !hideField(field); //field.toggleProperty || entity[field.toggleProperty] === (field.toggleValue || true);
            if (
                field.mandatory &&
                shown &&
                !validValue &&
                !(field.type === 'number' && value === 0) &&
                !(field.allowZeros && value === 0)
            ) {
                errors[field.dataProperty] = SystemText('Admin/General/Modal/Errors/Missing', field.label.toLowerCase());
                result = false;
            } else if (value && field.regEx && !checkRegEx(value, field.regEx, field.regExPerLine)) {
                errors[field.dataProperty] = SystemText('Admin/General/Modal/Errors/Invalid', field.label.toLowerCase());
                result = false;
            } else if (value && field.validation && field.validation(entity)) {
                errors[field.dataProperty] = field.validation(entity);
                result = false;
            }
        });

        setErrors(errors);

        return result;
    };

    const saveItem = action => {
        const newEntity = { ...entity, ...values };

        if (validateEntity(newEntity)) {
            options.prepareForSave && options.prepareForSave(newEntity);
            api[action](
                newEntity,
                () => {
                    setSaving(false);
                    !subAdd && setShow(false);
                    subAdd ? cancel() : load();
                },
                err => {
                    setSaving(false);
                    const temp = { ...errors };
                    if (err?.property) {
                        temp[err.property] = err.value;
                        setErrors(temp);
                    }
                }
            );
        } else {
            setSaving(false);
        }
    };

    const removeItem = (id, deleting) => {
        remove(
            id,
            () => {
                dispatch(
                    setShowSuccess(
                        SystemText(
                            `Admin/General/Deleted${id.length > 1 ? '/Plural' : ''}`,
                            controlOptions.label.toLowerCase()
                        )
                    )
                );
                load();
            },
            deleting
        );
    };

    const sort = sortBy => {
        let ascending = true;

        if (controlOptions.sortBy === sortBy) {
            ascending = !controlOptions.ascending;
        }

        const options = {
            ...controlOptions,
            sortBy,
            ascending,
            page: 1,
        };

        setControlOptions(options);
    };

    const goToPage = page => {
        const options = {
            ...controlOptions,
            page,
        };

        setControlOptions(options);
    };

    const filter = value => {
        goToPage(1);
        setSearch(value);
    };

    const clean = s => (s ? s.toString().trim().toLowerCase() : '');
    const numberfy = s => (isNaN(s) ? 0 : parseFloat(s));

    const fieldIsCount = field => columns.some(c => c.showCount && c.dataProperty === field);
    const fieldIsNumeric = field => columns.some(c => c.isNumeric && c.dataProperty === field);
    const columnSortFunc = columns?.find(c => c.dataProperty === controlOptions.sortBy)?.sortByFunction;

    const compare = (a, b) => {
        if (fieldIsCount(controlOptions.sortBy)) {
            if (!a[controlOptions.sortBy]) a[controlOptions.sortBy] = [];
            if (!b[controlOptions.sortBy]) b[controlOptions.sortBy] = [];
            if (a[controlOptions.sortBy].length < b[controlOptions.sortBy].length) return controlOptions.ascending ? -1 : 1;
            if (a[controlOptions.sortBy].length > b[controlOptions.sortBy].length) return controlOptions.ascending ? 1 : -1;
        } else if (fieldIsNumeric(controlOptions.sortBy)) {
            if (numberfy(a[controlOptions.sortBy]) < numberfy(b[controlOptions.sortBy]))
                return controlOptions.ascending ? -1 : 1;
            if (numberfy(a[controlOptions.sortBy]) > numberfy(b[controlOptions.sortBy]))
                return controlOptions.ascending ? 1 : -1;
        } else {
            if (clean(a[controlOptions.sortBy]) < clean(b[controlOptions.sortBy])) return controlOptions.ascending ? -1 : 1;
            if (clean(a[controlOptions.sortBy]) > clean(b[controlOptions.sortBy])) return controlOptions.ascending ? 1 : -1;
        }
        return 0;
    };

    const toggledData = filterColumn
        ? data.filter(d => {
              return filterToggle === false || d[filterColumn] === filterToggle;
          })
        : data;

    const filteredData = subAdd
        ? []
        : search
          ? toggledData.filter(d => {
                let result = false;
                columns
                    .filter(c => !c.doNotSearch)
                    .forEach(c => {
                        if (c.searchPropertyIsArray && ArrayHelpers.existsAndHasLength(d[c.searchProperty])) {
                            d[c.searchProperty].map(x => {
                                result = result || (x || '').toLowerCase().indexOf(search.toLowerCase()) >= 0;
                            });
                        } else if (typeof d[c.dataProperty] === 'string') {
                            result =
                                result ||
                                d[c.dataProperty].toLowerCase().indexOf(search.toLowerCase()) >= 0 ||
                                (c.searchProperty &&
                                    (d[c.searchProperty] || '').toLowerCase().indexOf(search.toLowerCase()) >= 0);
                        } else {
                            result =
                                result ||
                                (c.searchProperty &&
                                    (d[c.searchProperty] || '').toLowerCase().indexOf(search.toLowerCase()) >= 0);
                        }
                    });

                return result;
            })
          : toggledData;

    controlOptions.recordCount = filteredData ? filteredData.length : controlOptions.recordCount;
    const start = (controlOptions.page - 1) * controlOptions.pageSize + 1;
    const end =
        start + controlOptions.pageSize > controlOptions.recordCount
            ? controlOptions.recordCount
            : start + controlOptions.pageSize - 1;

    const sortedData = columnSortFunc ? columnSortFunc(filteredData, controlOptions.ascending) : filteredData.sort(compare);

    const processedData = sortedData.reduce((result, d, i) => {
        if (i + 1 >= start && i + 1 <= end) result.push(d);
        return result;
    }, []);

    const title = values?.id
        ? SystemText('Admin/General/Modal/Title/Edit', controlOptions.label)
        : SystemText('Admin/General/Modal/Title/Add', controlOptions.label);

    React.useEffect(() => {
        // If deleting last item off last page, go back 1 page
        if (!ArrayHelpers.existsAndHasLength(processedData) && controlOptions?.page > 1) {
            setControlOptions({ ...controlOptions, page: controlOptions?.page - 1 });
        }
    }, [processedData, controlOptions?.page]);

    const footer = (
        <div>
            {!values.id && (
                <SaveButton
                    setSaving={setSaving}
                    saving={saving}
                    className="btn btn-success"
                    data-testid="modal-button-add"
                    onClick={() => saveItem('add')}
                >
                    {SystemText('Admin/General/Modal/Buttons/Add')}
                </SaveButton>
            )}
            {values.id && (
                <SaveButton
                    saving={saving}
                    setSaving={setSaving}
                    className="btn btn-success"
                    data-testid="modal-button-update"
                    onClick={() => saveItem('update')}
                >
                    {SystemText('Admin/General/Modal/Buttons/Update')}
                </SaveButton>
            )}
            <button className="btn" data-testid="modal-button-cancel" onClick={() => cancel()}>
                {SystemText('Admin/General/Modal/Buttons/Cancel')}
            </button>
        </div>
    );

    const hideField = field => {
        const toggleField = field.toggleProperty && fields.find(f => f.dataProperty === field.toggleProperty);
        if (!field) return false;
        if (field.addOnly && values.id) return true;
        if (field.editOnly && !values.id) return true;
        if (field.hideField && field.hideField(values, entity)) return true;
        if (toggleField && hideField(toggleField)) return true;
        if (field.toggleProperty && !field.toggleValue && !values[field.toggleProperty]) return true;
        if (field.toggleProperty && ArrayHelpers.existsAndHasLength(field.toggleValue)) {
            return !field.toggleValue.some(x => x === values[field.toggleProperty]);
        }
        return field.toggleProperty && field.toggleValue && values[field.toggleProperty] !== field.toggleValue;
    };

    const mappedField = field => {
        if (hideField(field)) return null;
        let thisValue = values[field.dataProperty];
        if (field.dataRef) {
            thisValue = thisValue?.filter(v => field.data.some(d => d.id === v));
        }

        const ref = `field-${field.dataProperty}-${field.dataRef || 'form'}`.toLowerCase();

        const fieldProps = {
            key: ref,
            id: ref,
            'data-testid': ref,
            onChange: value => {
                if (field.onChange) {
                    field.onChange(value);
                }
                updateProperty(value, field.dataProperty, field.dataRef ? field.data : null);
            },
            onSubmit: () => (values.id ? saveItem('update') : saveItem('add')),
            value: field.type === 'label' && !thisValue ? '-' : thisValue,
            error:
                errors[field.dataProperty] ||
                (field[errorType]
                    ? field[`${errorType}_Text`]
                        ? field[`${errorType}_Text`]
                        : errorType === 'uniqueErrors'
                          ? SystemText('Admin/General/Errors/Unique', controlOptions.label.toLowerCase())
                          : SystemText('Admin/General/Errors/Standard')
                    : ''),
            placeholder: field.placeholder
                ? field.placeholder
                : SystemText('Admin/General/Defaults/Placeholder', field.label?.toLowerCase()),
            dataBlockValue: field.dataBlock ? values[field.dataBlock] : '',
            readonly: typeof field.readonly === 'function' ? field.readonly(values, entity || {}) : field.readonly,
            data: typeof field.data === 'function' ? field.data(values, entity) : field.data,
        };

        if (entity?.id && field.nonEditable) {
            return <Forms.Label {...field} {...fieldProps} />;
        }

        switch (field.type) {
            case 'select':
                if (fieldProps.data?.length === 0 && field.hideIfNoData) return null;
                return <Forms.Select {...field} {...fieldProps} />;
            case 'text':
            case 'email':
            case 'number':
                return <Forms.Text {...field} {...fieldProps} />;
            case 'file':
                return <Forms.File config={config} {...field} {...fieldProps} />;
            case 'image':
                return (
                    <Forms.Image
                        {...field}
                        {...fieldProps}
                        images={images}
                        addImage={(f, s) => dispatch(addImage(f, s))}
                        config={config}
                        onMetaChange={value => updateProperty(value, `${field.dataProperty}Meta`)}
                    />
                );
            case 'tags':
                return (
                    <Forms.Tags
                        {...field}
                        {...fieldProps}
                        readonlyTags={field.readonlyTagsProperty ? values[field.readonlyTagsProperty] : undefined}
                    />
                );
            case 'textarea':
                return <Forms.Textarea tags={tags} handles={handles} {...field} {...fieldProps} />;
            case 'toggle':
                return <Forms.Toggle {...field} {...fieldProps} />;
            case 'date':
                return <Forms.Date {...field} {...fieldProps} />;
            case 'buttons':
                return (
                    <Forms.Buttons {...field} {...fieldProps}>
                        <div className="btn-row">
                            {field.buttons.map((b, i) => {
                                if (b.show && !b.show(values)) {
                                    if (values[field.dataProperty] === b.value) {
                                        fieldProps.onChange(b.fallback);
                                    }

                                    return null;
                                }

                                return (
                                    <button
                                        key={`button-${field.dataProperty}-${i}`}
                                        className={
                                            values[field.dataProperty]?.toLowerCase() === b.value?.toLowerCase()
                                                ? 'btn btn-primary'
                                                : 'btn btn-default'
                                        }
                                        onClick={() => fieldProps.onChange(b.value)}
                                        disabled={fieldProps.readonly}
                                    >
                                        {b.label}
                                    </button>
                                );
                            })}
                        </div>
                    </Forms.Buttons>
                );
            case 'special':
                return field.renderer(values, fieldProps, field);
            case 'label':
                return (
                    <Forms.Label
                        {...field}
                        {...fieldProps}
                        value={Array.isArray(fieldProps.value) ? fieldProps.value.join(', ') : fieldProps.value}
                    />
                );
            default:
                return null;
        }
    };

    const addNewButton = () => {
        let btns = blockAddNew
            ? []
            : [{ label: controlOptions.addNewLabel || controlOptions.label, override: { ...defaultValues } }];

        if (ArrayHelpers.existsAndHasLength(extraAddButtons)) {
            btns = btns.concat(extraAddButtons);
        }

        const addButtons =
            btns.length > 1 ? (
                <div style={{ display: 'inline-block' }} className="mr5">
                    <FloatingMenu
                        className="btn-success"
                        icon="plus"
                        buttonText={SystemText('Admin/General/Buttons/AddNew', ' ')}
                    >
                        {btns.map((b, i) => (
                            <li
                                key={`extra-button-${i}`}
                                data-testid={`extra-button-${i}`}
                                onClick={() => openAddModal({ ...defaultValues, ...b.override })}
                            >
                                {b.label}
                            </li>
                        ))}
                    </FloatingMenu>
                </div>
            ) : btns.length === 1 ? (
                <button
                    id="add-new"
                    data-testid="button-add-new"
                    className="btn btn-success"
                    onClick={() => openAddModal({ ...btns[0].override })}
                >
                    <i className="fa fa-plus mr10" />
                    {SystemText('Admin/General/Buttons/AddNew', btns[0].label)}
                </button>
            ) : null;

        return (
            <div>
                {addButtons}
                {extraButtons}
            </div>
        );
    };

    const filterFields = () => (
        <div className="row">
            <div className="col-md-12">
                <Forms.Text
                    id="filter-box"
                    data-testid="text-filter-field"
                    label={SystemText('Admin/General/Search/Label')}
                    onChange={value => {
                        goToPage(1);
                        filter(value);
                    }}
                    placeholder={filterHelp || SystemText('Admin/General/Search/Placeholder')}
                    value={search}
                    icon="fa-search"
                />
            </div>
        </div>
    );

    const toggle = () =>
        filterColumn ? (
            <label className={filterToggle ? 'switch-on' : 'switch-off'}>
                <span>{filterTextOn}?</span>
                <Switch
                    onChange={value => {
                        goToPage(1);
                        setFilterToggle(value);
                    }}
                    checked={filterToggle}
                />
            </label>
        ) : null;

    const pageTitle = (label, plural) => {
        return SystemText('Admin/General/Title', plural || StringHelpers.pluralise(label));
    };

    const typeClassMap = {
        warning: 'warning',
        error: 'danger',
        success: 'success',
        default: 'warning',
    };

    return (
        <div data-testid="admin-title">
            {!subAdd && (
                <PageHeader
                    title={pageTitle(controlOptions.label, controlOptions.labelPlural)}
                    extra={addNewButton}
                    adminMenu
                />
            )}
            <section className="content mtm10">
                {bannerMessages?.map((m, i) => (
                    <div
                        key={`banner-message-${i}`}
                        className={`banner solid p2 mb5 ${typeClassMap[m.type] ?? typeClassMap.default}`}
                    >
                        {m.icon && <i className={`p2 pr5 fa fa-${m.icon}`} />}
                        {m.text}&nbsp;
                        {m.link}
                    </div>
                ))}

                {!subAdd && <SortFilter toggle={toggle} filterFields={filterFields} filterBoxClassName="filter-raw" />}
                <Modal
                    key={`modal-${entity?.id}`}
                    id="add-new-modal"
                    show={subAdd ? showModal : show}
                    modalInModal={subAdd}
                    title={title}
                    close={closeModal}
                    footer={footer}
                    toggleFormHelp={() => dispatch(toggleFormHelp())}
                >
                    {formPrefix && <div>{formPrefix}</div>}
                    {fields
                        .filter(f => !f.inTitleBar)
                        .map((field, i) => {
                            return <div key={`field-intitle-${i}`}>{mappedField(field)}</div>;
                        })}
                    {fields.some(f => f.inTitleBar) && (
                        <TitleBar
                            title={SystemText('Admin/General/More')}
                            data-testid="span-show-more"
                            openTitle={SystemText('Admin/General/Less')}
                            className="showMore"
                        >
                            {fields
                                .filter(f => f.inTitleBar)
                                .map((field, i) => {
                                    return <div key={`field-${i}`}>{mappedField(field)}</div>;
                                })}
                        </TitleBar>
                    )}
                </Modal>
                {!subAdd && (
                    <span data-testid="admin-table-area">
                        {loading || adminLoading ? (
                            <Loading />
                        ) : (
                            <Table
                                data={processedData}
                                columns={columns.filter(c => !c.hide)}
                                options={controlOptions}
                                setCurrent={
                                    controlOptions.allowSetCurrent
                                        ? d => {
                                              if (onUpdateLoad) {
                                                  onUpdateLoad(d);
                                              }
                                              setCurrent(d);
                                          }
                                        : null
                                }
                                removeItem={(id, data) => removeItem(id, data)}
                                activateItems={(ids, data, value) =>
                                    ids.forEach((id, idx) => {
                                        const item = data.find(d => d.id === id);
                                        item.active = value;
                                        update(
                                            { ...item },
                                            ids.length - 1 === idx && load
                                                ? () => {
                                                      dispatch(
                                                          setShowSuccess(
                                                              SystemText(
                                                                  'Admin/General/Saved/Plural',
                                                                  options.label.toLowerCase()
                                                              )
                                                          )
                                                      );
                                                      load();
                                                  }
                                                : null,
                                            true
                                        );
                                    })
                                }
                                sort={column => sort(column)}
                                goToPage={page => goToPage(page)}
                            />
                        )}
                    </span>
                )}
            </section>
        </div>
    );
};

export default SimpleAdmin;
